import { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import { MoreHorizontal, Edit, Trash2, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { TOURNAMENT_STATUS_BADGE_MAP } from '@/modules/tournament/constants'
import type { components } from '@/schema'

type Tournament = components['schemas']['AdminTournamentResponse']

export const createTournamentColumns = (
	onDelete: (id: string) => void,
): ColumnDef<Tournament>[] => [
	{
		accessorKey: 'bannerImage',
		header: 'แบนเนอร์',
		cell: ({ row }) => {
			const tournament = row.original
			if (!tournament.bannerImage) {
				return (
					<div className="flex h-16 w-24 items-center justify-center rounded-md bg-muted">
						<span className="text-xs text-muted-foreground">ไม่มีรูป</span>
					</div>
				)
			}
			return (
				<img
					src={tournament.bannerImage}
					alt={tournament.name}
					className="h-16 w-24 rounded-md object-cover"
				/>
			)
		},
	},
	{
		accessorKey: 'name',
		header: 'ชื่อทัวร์นาเมนต์',
		cell: ({ row }) => {
			const tournament = row.original
			return (
				<div>
					<div className="font-medium">{tournament.name}</div>
					{tournament.description && (
						<div className="text-xs text-muted-foreground">
							{tournament.description}
						</div>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'status',
		header: 'สถานะ',
		cell: ({ row }) => {
			const status = row.original.status
			const variant = TOURNAMENT_STATUS_BADGE_MAP[status] || 'default'
			const label =
				status === 'DRAFT'
					? 'ฉบับร่าง'
					: status === 'ACTIVE'
						? 'กำลังดำเนินการ'
						: status === 'ENDED'
							? 'สิ้นสุดแล้ว'
							: 'ยกเลิก'
			return <Badge variant={variant}>{label}</Badge>
		},
	},
	{
		accessorKey: 'startDate',
		header: 'วันที่เริ่ม',
		cell: ({ row }) => {
			return format(new Date(row.original.startDate), 'dd MMM yyyy', {
				locale: th,
			})
		},
	},
	{
		accessorKey: 'endDate',
		header: 'วันที่สิ้นสุด',
		cell: ({ row }) => {
			return format(new Date(row.original.endDate), 'dd MMM yyyy', {
				locale: th,
			})
		},
	},
	{
		accessorKey: 'playerCount',
		header: 'จำนวนผู้เข้าร่วม',
		cell: ({ row }) => {
			return (
				<div className="text-center">{row.original.playerCount || 0} คน</div>
			)
		},
	},
	{
		accessorKey: 'minBetAmount',
		header: 'เดิมพันขั้นต่ำ',
		cell: ({ row }) => {
			return `฿${Number(row.original.minBetAmount).toLocaleString()}`
		},
	},
	{
		accessorKey: 'allowedProviders',
		header: 'ค่ายที่อนุญาต',
		cell: ({ row }) => {
			const providers = row.original.allowedProviders
			if (!providers || providers.length === 0) {
				return <span className="text-xs text-muted-foreground">ทุกค่าย</span>
			}
			return (
				<div className="flex flex-wrap gap-1">
					{providers.slice(0, 2).map((provider) => (
						<Badge key={provider.id} variant="outline" className="text-xs">
							{provider.displayName || provider.name}
						</Badge>
					))}
					{providers.length > 2 && (
						<Badge variant="secondary" className="text-xs">
							+{providers.length - 2}
						</Badge>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'allowedGames',
		header: 'เกมที่อนุญาต',
		cell: ({ row }) => {
			const games = row.original.allowedGames
			if (!games || games.length === 0) {
				return <span className="text-xs text-muted-foreground">ทุกเกม</span>
			}
			return (
				<div className="flex flex-wrap gap-1">
					{games.slice(0, 2).map((game) => (
						<Badge key={game.id} variant="outline" className="text-xs">
							{game.displayName || game.name}
						</Badge>
					))}
					{games.length > 2 && (
						<Badge variant="secondary" className="text-xs">
							+{games.length - 2}
						</Badge>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'isActive',
		header: 'เปิดใช้งาน',
		cell: ({ row }) => {
			return row.original.isActive ? (
				<Badge variant="default">เปิด</Badge>
			) : (
				<Badge variant="secondary">ปิด</Badge>
			)
		},
	},
	{
		id: 'actions',
		cell: ({ row }) => {
			const tournament = row.original
			return (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" className="h-8 w-8 p-0">
							<MoreHorizontal className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem asChild>
							<Link to={`/operator/tournaments/${tournament.id}/players`}>
								<Users className="mr-2 h-4 w-4" />
								ดูผู้เข้าร่วม
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link to={`/operator/tournaments/${tournament.id}/edit`}>
								<Edit className="mr-2 h-4 w-4" />
								แก้ไข
							</Link>
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className="text-destructive"
							onClick={() => onDelete(tournament.id)}
							disabled={tournament.status === 'ACTIVE'}
						>
							<Trash2 className="mr-2 h-4 w-4" />
							ลบ
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)
		},
	},
]
